import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CleanVideoPlayer from '../../components/feature/CleanVideoPlayer';
import SEOHead from '../../components/feature/SEOHead';
import { getWildCatVideos } from '../../mocks/catVideos';

interface SpeciesInfo {
  id: string;
  commonName: string;
  scientificName: string;
  conservationStatus: 'Critically Endangered' | 'Endangered' | 'Vulnerable' | 'Near Threatened' | 'Least Concern';
  habitat: string;
  weight: string;
  speed: string;
  lifespan: string;
  imageUrl: string;
  description: string;
}

const speciesData: SpeciesInfo[] = [
  {
    id: 'tiger',
    commonName: 'Tiger',
    scientificName: 'Panthera tigris',
    conservationStatus: 'Endangered',
    habitat: 'Tropical forests, grasslands, and mangrove swamps across Asia',
    weight: '220-660 lbs (100-300 kg)',
    speed: 'Up to 40 mph (65 km/h)',
    lifespan: '10-15 years in wild, up to 20 in captivity',
    imageUrl: 'https://public.readdy.ai/ai/img_res/79643b48af1dc559952d425e028dbee7.jpg',
    description: 'The largest cat species, known for their distinctive orange coat with black stripes.'
  },
  {
    id: 'lion',
    commonName: 'Lion',
    scientificName: 'Panthera leo',
    conservationStatus: 'Vulnerable',
    habitat: 'African savannas, grasslands, and open woodlands',
    weight: '265-420 lbs (120-190 kg)',
    speed: 'Up to 50 mph (80 km/h)',
    lifespan: '10-14 years in wild, up to 20 in captivity',
    imageUrl: 'https://readdy.ai/api/search-image?query=powerful%20male%20lion%20with%20magnificent%20golden%20mane%20resting%20on%20savanna%20grassland%20at%20sunset%20warm%20golden%20light%20african%20wildlife%20photography%20majestic%20king%20of%20jungle%20detailed%20realistic%20portrait&width=800&height=600&seq=lion001&orientation=landscape',
    description: 'Known as the "King of the Jungle", lions are the only cats that live in social groups called prides.'
  },
  {
    id: 'leopard',
    commonName: 'Leopard',
    scientificName: 'Panthera pardus',
    conservationStatus: 'Vulnerable',
    habitat: 'Sub-Saharan Africa, parts of Asia, forests to mountains',
    weight: '66-176 lbs (30-80 kg)',
    speed: 'Up to 36 mph (58 km/h)',
    lifespan: '12-17 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=elegant%20leopard%20with%20golden%20spotted%20coat%20resting%20on%20tree%20branch%20in%20african%20savanna%20dappled%20sunlight%20through%20leaves%20wildlife%20photography%20detailed%20rosette%20patterns%20realistic%20portrait&width=800&height=600&seq=leopard001&orientation=landscape',
    description: 'Incredibly adaptable cats known for their spotted coats and ability to climb trees with prey.'
  },
  {
    id: 'jaguar',
    commonName: 'Jaguar',
    scientificName: 'Panthera onca',
    conservationStatus: 'Near Threatened',
    habitat: 'Rainforests, swamps, and grasslands of Central and South America',
    weight: '100-250 lbs (45-113 kg)',
    speed: 'Up to 50 mph (80 km/h)',
    lifespan: '12-15 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=powerful%20jaguar%20with%20golden%20coat%20and%20black%20rosette%20spots%20walking%20through%20dense%20amazon%20rainforest%20lush%20green%20vegetation%20tropical%20setting%20wildlife%20photography%20muscular%20build%20realistic%20detailed&width=800&height=600&seq=jaguar001&orientation=landscape',
    description: 'The largest cat in the Americas with the strongest bite force of all big cats.'
  },
  {
    id: 'cheetah',
    commonName: 'Cheetah',
    scientificName: 'Acinonyx jubatus',
    conservationStatus: 'Vulnerable',
    habitat: 'African savannas and grasslands, small population in Iran',
    weight: '75-140 lbs (34-64 kg)',
    speed: 'Up to 70 mph (112 km/h) - fastest land animal',
    lifespan: '8-12 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=sleek%20cheetah%20with%20spotted%20coat%20in%20running%20stance%20on%20african%20savanna%20grassland%20golden%20sunset%20light%20distinctive%20black%20tear%20marks%20aerodynamic%20build%20wildlife%20photography%20realistic%20detailed%20portrait&width=800&height=600&seq=cheetah001&orientation=landscape',
    description: 'The fastest land animal, built for incredible speed with a slender, aerodynamic body.'
  },
  {
    id: 'snow-leopard',
    commonName: 'Snow Leopard',
    scientificName: 'Panthera uncia',
    conservationStatus: 'Vulnerable',
    habitat: 'Mountain ranges of Central and South Asia, high altitudes',
    weight: '60-120 lbs (27-55 kg)',
    speed: 'Up to 40 mph (64 km/h)',
    lifespan: '15-18 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=beautiful%20snow%20leopard%20with%20thick%20grey%20spotted%20fur%20and%20long%20tail%20in%20snowy%20mountain%20landscape%20himalayan%20peaks%20winter%20scenery%20wildlife%20photography%20soft%20lighting%20realistic%20detailed%20portrait&width=800&height=600&seq=snowleopard001&orientation=landscape',
    description: 'Elusive mountain cats with thick fur and long tails, perfectly adapted to cold, high-altitude environments.'
  },
  {
    id: 'lynx',
    commonName: 'Lynx',
    scientificName: 'Lynx lynx',
    conservationStatus: 'Least Concern',
    habitat: 'Forests and tundra across Europe, Asia, and North America',
    weight: '18-66 lbs (8-30 kg)',
    speed: 'Up to 50 mph (80 km/h)',
    lifespan: '14-17 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=eurasian%20lynx%20with%20tufted%20ears%20and%20spotted%20grey%20brown%20fur%20in%20snowy%20forest%20landscape%20pine%20trees%20winter%20setting%20wildlife%20photography%20distinctive%20ear%20tufts%20realistic%20detailed%20portrait&width=800&height=600&seq=lynx001&orientation=landscape',
    description: 'Medium-sized cats with distinctive tufted ears and short tails, excellent hunters in cold climates.'
  },
  {
    id: 'caracal',
    commonName: 'Caracal',
    scientificName: 'Caracal caracal',
    conservationStatus: 'Least Concern',
    habitat: 'Africa, Middle East, Central Asia - savannas and semi-deserts',
    weight: '18-42 lbs (8-19 kg)',
    speed: 'Up to 50 mph (80 km/h)',
    lifespan: '12-16 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=caracal%20wild%20cat%20with%20distinctive%20long%20black%20tufted%20ears%20and%20reddish%20tan%20coat%20in%20african%20savanna%20landscape%20golden%20hour%20lighting%20athletic%20build%20wildlife%20photography%20realistic%20detailed%20portrait&width=800&height=600&seq=caracal001&orientation=landscape',
    description: 'Known for their remarkable jumping ability and distinctive long, black-tufted ears.'
  },
  {
    id: 'serval',
    commonName: 'Serval',
    scientificName: 'Leptailurus serval',
    conservationStatus: 'Least Concern',
    habitat: 'Sub-Saharan African grasslands and wetlands',
    weight: '15-40 lbs (7-18 kg)',
    speed: 'Up to 50 mph (80 km/h)',
    lifespan: '10-12 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=serval%20wild%20cat%20with%20golden%20spotted%20coat%20long%20legs%20and%20large%20ears%20in%20african%20grassland%20savanna%20tall%20grass%20sunset%20lighting%20slender%20build%20wildlife%20photography%20realistic%20detailed%20portrait&width=800&height=600&seq=serval001&orientation=landscape',
    description: 'Slender cats with the longest legs relative to body size of any cat, excellent hunters.'
  },
  {
    id: 'clouded-leopard',
    commonName: 'Clouded Leopard',
    scientificName: 'Neofelis nebulosa',
    conservationStatus: 'Vulnerable',
    habitat: 'Forests of Southeast Asia and the Himalayas',
    weight: '25-50 lbs (11-23 kg)',
    speed: 'Up to 40 mph (64 km/h)',
    lifespan: '11-17 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=clouded%20leopard%20with%20distinctive%20cloud%20shaped%20markings%20on%20golden%20grey%20coat%20in%20tropical%20rainforest%20tree%20branches%20southeast%20asian%20jungle%20wildlife%20photography%20unique%20pattern%20realistic%20detailed&width=800&height=600&seq=cloudedleopard001&orientation=landscape',
    description: 'Named for their distinctive cloud-shaped markings, excellent climbers with the longest canine teeth relative to body size.'
  },
  {
    id: 'sand-cat',
    commonName: 'Sand Cat',
    scientificName: 'Felis margarita',
    conservationStatus: 'Least Concern',
    habitat: 'Deserts of North Africa, Middle East, and Central Asia',
    weight: '3-7 lbs (1.5-3.5 kg)',
    speed: 'Up to 25 mph (40 km/h)',
    lifespan: '13-14 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=adorable%20sand%20cat%20with%20pale%20sandy%20fur%20large%20ears%20and%20fluffy%20appearance%20in%20desert%20landscape%20sand%20dunes%20arid%20environment%20small%20wild%20cat%20wildlife%20photography%20cute%20realistic%20detailed%20portrait&width=800&height=600&seq=sandcat001&orientation=landscape',
    description: 'The only cat species that lives primarily in true deserts, with fur-covered paws to protect from hot sand.'
  },
  {
    id: 'pallas-cat',
    commonName: "Pallas's Cat",
    scientificName: 'Otocolobus manul',
    conservationStatus: 'Least Concern',
    habitat: 'Grasslands and montane steppes of Central Asia',
    weight: '5-10 lbs (2.5-4.5 kg)',
    speed: 'Up to 30 mph (48 km/h)',
    lifespan: '11-12 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=pallas%20cat%20with%20extremely%20fluffy%20grey%20fur%20round%20flat%20face%20and%20grumpy%20expression%20in%20rocky%20mountain%20steppe%20landscape%20central%20asian%20habitat%20thick%20coat%20wildlife%20photography%20adorable%20realistic%20detailed&width=800&height=600&seq=pallascat001&orientation=landscape',
    description: 'Small wild cats with extremely dense fur and a distinctive flat face, often appearing grumpy.'
  },
  {
    id: 'fishing-cat',
    commonName: 'Fishing Cat',
    scientificName: 'Prionailurus viverrinus',
    conservationStatus: 'Vulnerable',
    habitat: 'Wetlands and mangroves of South and Southeast Asia',
    weight: '11-35 lbs (5-16 kg)',
    speed: 'Up to 35 mph (56 km/h)',
    lifespan: '10-12 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=fishing%20cat%20with%20grey%20brown%20spotted%20coat%20near%20water%20wetland%20habitat%20mangrove%20swamp%20southeast%20asian%20landscape%20semi%20aquatic%20wild%20cat%20wildlife%20photography%20realistic%20detailed%20portrait&width=800&height=600&seq=fishingcat001&orientation=landscape',
    description: 'Unique cats that are excellent swimmers and hunt fish, with partially webbed paws.'
  },
  {
    id: 'black-footed-cat',
    commonName: 'Black-Footed Cat',
    scientificName: 'Felis nigripes',
    conservationStatus: 'Vulnerable',
    habitat: 'Arid regions of southern Africa',
    weight: '2-5 lbs (1-2.5 kg)',
    speed: 'Up to 30 mph (48 km/h)',
    lifespan: '10-12 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=tiny%20black%20footed%20cat%20with%20spotted%20tan%20coat%20and%20black%20paw%20pads%20in%20african%20grassland%20savanna%20smallest%20wild%20cat%20species%20adorable%20fierce%20hunter%20wildlife%20photography%20realistic%20detailed%20portrait&width=800&height=600&seq=blackfootedcat001&orientation=landscape',
    description: 'The smallest wild cat in Africa, but one of the most efficient hunters with a 60% success rate.'
  },
  {
    id: 'panther',
    commonName: 'Black Panther',
    scientificName: 'Panthera pardus / Panthera onca',
    conservationStatus: 'Vulnerable',
    habitat: 'Forests of Africa, Asia, and Americas (melanistic leopards/jaguars)',
    weight: '66-250 lbs (30-113 kg)',
    speed: 'Up to 36-50 mph (58-80 km/h)',
    lifespan: '12-17 years in wild',
    imageUrl: 'https://readdy.ai/api/search-image?query=black%20panther%20melanistic%20leopard%20with%20sleek%20black%20coat%20in%20dense%20tropical%20rainforest%20jungle%20mysterious%20lighting%20powerful%20build%20wildlife%20photography%20dramatic%20realistic%20detailed%20portrait&width=800&height=600&seq=panther001&orientation=landscape',
    description: 'Not a separate species, but melanistic (black) leopards or jaguars with stunning dark coats.'
  }
];

const wildcatsSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Wild Cats of the World - MeowCentral',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/wildcats`,
  description: 'Discover 15+ wild cat species from tigers and lions to sand cats and Pallas cats. Watch wildlife videos, learn conservation facts, and explore species profiles.',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Wild Cat Species',
    numberOfItems: 15,
  },
};

const WildCatsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [flippedCard, setFlippedCard] = useState<string | null>(null);

  const wildCatVideos = getWildCatVideos();

  const categories = [
    { id: 'all', label: 'All Wild Cats', species: [] },
    { id: 'big', label: 'Big Cats', species: ['tiger', 'lion', 'leopard', 'jaguar', 'cheetah', 'panther'] },
    { id: 'medium', label: 'Medium Cats', species: ['lynx', 'caracal', 'serval', 'snow-leopard', 'clouded-leopard'] },
    { id: 'small', label: 'Small Wild Cats', species: ['sand-cat', 'pallas-cat', 'fishing-cat', 'black-footed-cat'] }
  ];

  const filteredVideos = selectedCategory === 'all' 
    ? wildCatVideos 
    : wildCatVideos.filter(video => {
        const category = categories.find(cat => cat.id === selectedCategory);
        return category?.species.some(species => 
          video.title.toLowerCase().includes(species.replace('-', ' '))
        );
      });

  const funFacts = [
    "Tigers are the largest wild cats in the world, weighing up to 660 pounds!",
    "Cheetahs can accelerate from 0 to 60 mph in just 3 seconds - faster than most sports cars!",
    "Snow leopards can jump up to 50 feet in a single leap across mountain crevasses.",
    "A lion's roar can be heard from up to 5 miles away across the African savanna.",
    "Black panthers aren't a separate species - they're leopards or jaguars with melanistic coloring.",
    "Sand cats can survive without drinking water, getting all moisture from their prey.",
    "Pallas's cats have the longest and densest fur of any cat species to survive -50°F temperatures.",
    "Fishing cats have partially webbed paws and are excellent swimmers who hunt underwater.",
    "Black-footed cats are Africa's smallest wild cat but have a 60% hunting success rate - the highest of any cat!",
    "Clouded leopards have the longest canine teeth relative to body size of any living cat species."
  ];

  useState(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % funFacts.length);
    }, 5000);
    return () => clearInterval(interval);
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critically Endangered':
        return 'bg-red-600';
      case 'Endangered':
        return 'bg-red-500';
      case 'Vulnerable':
        return 'bg-orange-500';
      case 'Near Threatened':
        return 'bg-yellow-500';
      case 'Least Concern':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filterBySpecies = (speciesId: string) => {
    const category = categories.find(cat => cat.species.includes(speciesId));
    if (category) {
      setSelectedCategory(category.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Wild Cats of the World - Species, Videos & Conservation | MeowCentral"
        description="Explore 15+ wild cat species including tigers, lions, leopards, cheetahs, and rare small cats. Watch wildlife videos, discover conservation facts, and learn about each species."
        keywords="wild cats, big cats, tiger, lion, leopard, cheetah, snow leopard, wild cat species, wildlife conservation, wild cat videos"
        canonical="/wildcats"
        schema={wildcatsSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <div className="relative h-screen w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20wild%20cat%20collection%20montage%20featuring%20tiger%20lion%20leopard%20cheetah%20snow%20leopard%20in%20natural%20habitats%20epic%20wildlife%20photography%20cinematic%20lighting%20majestic%20powerful%20animals%20diverse%20landscapes%20professional%20detailed%20realistic&width=1920&height=1080&seq=wildcat-hero-001&orientation=landscape"
          alt="Wild Cats Hero"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6">
          <h1 className="text-7xl font-bold mb-6 animate-fade-in text-center">
            Wild Cats of the World
          </h1>
          <p className="text-2xl mb-12 max-w-3xl text-center opacity-90">
            Discover the magnificent diversity of wild felines across the globe
          </p>
          
          <div className="flex gap-12 text-center">
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="text-5xl font-bold mb-2">15+</div>
              <div className="text-lg opacity-80">Species Featured</div>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="text-5xl font-bold mb-2">{wildCatVideos.length}</div>
              <div className="text-lg opacity-80">Videos</div>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <div className="text-5xl font-bold mb-2">60%</div>
              <div className="text-lg opacity-80">Need Conservation</div>
            </div>
          </div>
        </div>
      </div>

      {/* Fun Fact Ticker */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 py-4 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center gap-3 text-white">
            <i className="ri-lightbulb-flash-line text-2xl"></i>
            <span className="text-lg font-semibold">Did You Know?</span>
            <span className="text-lg animate-fade-in" key={currentFactIndex}>
              {funFacts[currentFactIndex]}
            </span>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex gap-3 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-2 rounded-full font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-amber-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="group relative bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
              onClick={() => setSelectedVideo(video.videoUrl)}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-3xl text-amber-500"></i>
                  </div>
                </div>
                <div className="absolute top-3 left-3 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {video.category}
                </div>
                <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs">
                  {video.duration}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{video.title}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i>
                    {video.likes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Meet the Species Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Meet the Species</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore detailed information about each magnificent wild cat species featured in our collection
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {speciesData.map((species) => (
              <div
                key={species.id}
                className="relative group perspective-1000"
                onMouseEnter={() => setFlippedCard(species.id)}
                onMouseLeave={() => setFlippedCard(null)}
              >
                <div className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${
                  flippedCard === species.id ? 'rotate-y-180' : ''
                }`}>
                  {/* Front of Card */}
                  <div className="absolute inset-0 backface-hidden">
                    <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow h-full">
                      <div className="relative h-64 overflow-hidden">
                        <img
                          src={species.imageUrl}
                          alt={species.commonName}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3">
                          <span className={`${getStatusColor(species.conservationStatus)} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg`}>
                            {species.conservationStatus}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-2xl font-bold text-gray-900 mb-1">{species.commonName}</h3>
                        <p className="text-sm italic text-gray-600 mb-3">{species.scientificName}</p>
                        <p className="text-sm text-gray-700 mb-4 line-clamp-2">{species.description}</p>
                        <div className="flex items-center justify-center">
                          <span className="text-xs text-gray-500">Hover for details</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back of Card */}
                  <div className="absolute inset-0 backface-hidden rotate-y-180">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg h-full p-6 text-white flex flex-col justify-between">
                      <div>
                        <h3 className="text-2xl font-bold mb-4">{species.commonName}</h3>
                        
                        <div className="space-y-3 text-sm">
                          <div className="flex items-start gap-2">
                            <i className="ri-map-pin-line text-lg flex-shrink-0 mt-0.5"></i>
                            <div>
                              <div className="font-semibold mb-1">Habitat</div>
                              <div className="opacity-90">{species.habitat}</div>
                            </div>
                          </div>

                          <div className="flex items-start gap-2">
                            <i className="ri-scales-line text-lg flex-shrink-0 mt-0.5"></i>
                            <div>
                              <div className="font-semibold mb-1">Weight</div>
                              <div className="opacity-90">{species.weight}</div>
                            </div>
                          </div>

                          <div className="flex items-start gap-2">
                            <i className="ri-speed-line text-lg flex-shrink-0 mt-0.5"></i>
                            <div>
                              <div className="font-semibold mb-1">Speed</div>
                              <div className="opacity-90">{species.speed}</div>
                            </div>
                          </div>

                          <div className="flex items-start gap-2">
                            <i className="ri-time-line text-lg flex-shrink-0 mt-0.5"></i>
                            <div>
                              <div className="font-semibold mb-1">Lifespan</div>
                              <div className="opacity-90">{species.lifespan}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          filterBySpecies(species.id);
                        }}
                        className="w-full bg-white text-amber-600 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-play-circle-line text-xl"></i>
                        Watch Videos
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conservation Awareness Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Wild Cats Matter</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Wild cats are apex predators that play a crucial role in maintaining healthy ecosystems. 
              Their survival is threatened, but together we can make a difference.
            </p>
          </div>

          {/* Conservation Stats Counter */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 text-center border-2 border-orange-200">
              <div className="text-5xl font-bold text-orange-600 mb-2">~3,900</div>
              <div className="text-gray-700 font-medium">Wild Tigers Remaining</div>
              <div className="text-sm text-gray-500 mt-2">Down from 100,000 in 1900</div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 text-center border-2 border-red-200">
              <div className="text-5xl font-bold text-red-600 mb-2">68%</div>
              <div className="text-gray-700 font-medium">Habitat Lost</div>
              <div className="text-sm text-gray-500 mt-2">In the last 50 years</div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center border-2 border-amber-200">
              <div className="text-5xl font-bold text-amber-600 mb-2">7</div>
              <div className="text-gray-700 font-medium">Species Critically Endangered</div>
              <div className="text-sm text-gray-500 mt-2">Urgent action needed</div>
            </div>
          </div>

          {/* Info Blocks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Ecosystem Role */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-plant-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ecosystem Guardians</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                As apex predators, wild cats regulate prey populations and maintain the balance of their ecosystems. 
                Their presence indicates a healthy, thriving environment.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Control herbivore populations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Prevent overgrazing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-green-600 mt-1 mr-2"></i>
                  <span>Maintain biodiversity</span>
                </li>
              </ul>
            </div>

            {/* Threats */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-200">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-alert-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Critical Threats</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Wild cats face unprecedented challenges from human activities. 
                Understanding these threats is the first step toward conservation.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-2"></i>
                  <span>Habitat loss & fragmentation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-2"></i>
                  <span>Illegal poaching & wildlife trade</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-2"></i>
                  <span>Climate change impacts</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mt-1 mr-2"></i>
                  <span>Human-wildlife conflict</span>
                </li>
              </ul>
            </div>

            {/* How to Help */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">How You Can Help</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Every action counts. Support organizations dedicated to wild cat conservation 
                and make a real difference in their survival.
              </p>
              <div className="space-y-3">
                <a 
                  href="https://www.worldwildlife.org/species/directory?direction=desc&sort=extinction_status" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white rounded-lg px-4 py-3 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <span className="font-medium text-gray-900">World Wildlife Fund</span>
                  <i className="ri-external-link-line text-blue-600"></i>
                </a>
                <a 
                  href="https://www.panthera.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white rounded-lg px-4 py-3 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <span className="font-medium text-gray-900">Panthera</span>
                  <i className="ri-external-link-line text-blue-600"></i>
                </a>
                <a 
                  href="https://snowleopard.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-between bg-white rounded-lg px-4 py-3 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <span className="font-medium text-gray-900">Snow Leopard Trust</span>
                  <i className="ri-external-link-line text-blue-600"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Clean Video Player Modal */}
      {selectedVideo && (
        <CleanVideoPlayer
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
};

export default WildCatsPage;